if (!customElements.get('mo-book-finder-shop')) {
  customElements.define('mo-book-finder-shop', class extends HTMLElement {
    static allowed = { mood: ['romance', 'suspense', 'mystery', 'christmas'], format: ['novella', 'standalone', 'series'] };

    static clean(value) {
      const input = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      const mood = Array.isArray(input.mood) ? [...new Set(input.mood.filter(item => this.allowed.mood.includes(item)))] : [];
      const format = Array.isArray(input.format) ? [...new Set(input.format.filter(item => this.allowed.format.includes(item)))] : [];
      return {
        mood, format,
        scifi: ['any', 'no', 'yes'].includes(input.scifi) ? input.scifi : 'any',
        read: input.read === 'read' ? 'read' : 'new',
        availableOnly: input.availableOnly === true,
        sort: ['recommended', 'price_asc', 'price_desc'].includes(input.sort) ? input.sort : 'recommended'
      };
    }

    static select(books, input, covered = []) {
      const filters = this.clean(input);
      const excluded = new Set(covered);
      const candidates = books.filter(book => {
        if (excluded.has(book.productId) || (filters.availableOnly && !book.available)) return false;
        if (book.volume === '2' && filters.read !== 'read') return false;
        if (book.volume === '1' && filters.read === 'read') return false;
        if (filters.scifi === 'any') return true;
        if (book.scifi === 'unknown' || !Number.isFinite(Number(book.scifi))) return false;
        return filters.scifi === 'no' ? Number(book.scifi) === 0 : Number(book.scifi) > 0;
      }).map(book => {
        const differences = [];
        if (filters.mood.length && !filters.mood.some(mood => book.moods.includes(mood))) differences.push('mood');
        if (filters.format.length && !filters.format.includes(book.format)) differences.push('format');
        return { book, differences };
      });
      const moodHits = book => filters.mood.filter(mood => book.moods.includes(mood)).length;
      const sort = (a, b) => {
        if (filters.sort === 'price_asc') return a.price - b.price || a.index - b.index;
        if (filters.sort === 'price_desc') return b.price - a.price || a.index - b.index;
        return Number(b.available) - Number(a.available) || moodHits(b) - moodHits(a) || a.index - b.index;
      };
      const exact = candidates.filter(item => !item.differences.length).map(item => item.book).sort(sort);
      const different = candidates.filter(item => item.differences.length);
      const minimum = Math.min(...different.map(item => item.differences.length));
      const relaxed = different.filter(item => item.differences.length === minimum).sort((a, b) => sort(a.book, b.book));
      return { exact, relaxed };
    }

    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      this.labels = Object.fromEntries(Object.entries(JSON.parse(this.querySelector('[data-labels]').textContent)).map(([key, value]) => [key, value.replaceAll('&amp;', '&')]));
      this.cart = JSON.parse(this.querySelector('[data-cart]').textContent);
      this.coverage = JSON.parse(this.querySelector('[data-coverage]').textContent);
      this.params = new URLSearchParams(window.location.search);
      this.previewId = this.params.get('preview_theme_id') || (window.Shopify?.theme?.role !== 'main' ? String(window.Shopify?.theme?.id || '') : '');
      this.fromCart = this.params.get('finder_source') === 'cart';
      this.fromBag = ['cart', 'empty_cart'].includes(this.params.get('finder_source'));
      this.storageKey = 'mo-finder:shop:1:' + (window.Shopify?.theme?.id || '') + ':' + this.id;
      this.books = [...this.querySelectorAll('[data-book]')].map((element, index) => {
        const offers = JSON.parse(element.querySelector('[data-offers]').textContent);
        const available = offers.filter(offer => offer.available);
        return {
          element, offers, index,
          id: element.dataset.id, productId: element.dataset.productId,
          title: element.querySelector('.mf-card-title').textContent.trim(), hook: element.dataset.hook || '',
          url: element.querySelector('.mf-card-title a').getAttribute('href'),
          format: element.dataset.format, scifi: element.dataset.scifi, volume: element.dataset.volume,
          moods: element.dataset.moods.trim().split(/\s+/).filter(Boolean),
          available: available.length > 0,
          price: Math.min(...(available.length ? available : offers).map(offer => offer.price)),
          imperfects: JSON.parse(element.querySelector('[data-imperfects-json]')?.textContent || '[]')
        };
      });
      this.editions = {};
      this.dedications = {};
      this.relaxedOpen = false;
      this.filters = this.constructor.clean();
      const saved = this.readSaved();
      if (saved) {
        this.filters = this.constructor.clean(saved.filters);
        for (const book of this.books) if (book.offers.some(offer => offer.id === saved.editions?.[book.id])) this.editions[book.id] = saved.editions[book.id];
      }
      if (this.params.has('bf')) this.filters = this.filtersFromUrl();
      this.grid = this.querySelector('[data-grid]');
      this.active = this.querySelector('[data-active]');
      this.empty = this.querySelector('[data-empty]');
      this.relaxed = this.querySelector('[data-relaxed]');
      this.relaxedGrid = this.querySelector('[data-relaxed-grid]');
      this.filterPanel = this.querySelector('[data-filter-panel]');
      this.filterDialog = this.querySelector('[data-filter-dialog]');
      this.buyDialog = this.querySelector('[data-buy-dialog]');
      this.buyForm = this.querySelector('[data-buy-form]');
      this.variantInput = this.querySelector('[data-buy-variant]');
      this.dedicationInput = this.querySelector('[data-dedication]');
      this.editionsContainer = this.querySelector('[data-editions]');
      this.sortInput = this.querySelector('[data-sort]');
      this.status = this.querySelector('[data-cart-status]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.header = document.querySelector('.section-header');
      this.placeFilters();
      this.listen(this.mobile, 'change', () => { this.placeFilters(); this.syncHeaderOffset(); });
      this.listen(window, 'scroll', () => this.syncHeaderOffset(), { passive: true });
      this.listen(window, 'resize', () => this.syncHeaderOffset());
      if (this.header) this.listen(this.header, 'transitionend', () => this.syncHeaderOffset());
      this.syncHeaderOffset();
      this.listen(this, 'click', event => this.onClick(event));
      this.listen(this, 'change', event => this.onChange(event));
      this.listen(this.dedicationInput, 'input', () => {
        if (this.currentBook) this.dedications[this.currentBook.id] = this.dedicationInput.value;
      });
      this.listen(window, 'popstate', () => {
        this.filters = this.filtersFromUrl(); this.relaxedOpen = false; this.render(); this.persist();
      });
      for (const dialog of [this.filterDialog, this.buyDialog]) {
        this.listen(dialog, 'close', () => this.afterDialogClose());
        this.listen(dialog, 'click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, data => {
          if (data.source === 'mo-cart-add' && this.buyDialog.open) {
            this.cartReturnTrigger = this.lastTrigger;
            this.buyDialog.close();
            document.querySelector('cart-drawer')?.setActiveElement(this.cartReturnTrigger);
          }
          this.refreshCart();
        });
      }
      this.listen(document, 'visibilitychange', () => {
        if (document.hidden) this.lastHidden = Date.now();
        else if (this.lastHidden && Date.now() - this.lastHidden > 60000) window.location.reload();
      });
      this.listen(window, 'pageshow', event => { if (event.persisted) window.location.reload(); });
      const back = this.querySelector('[data-return]');
      const returnUrl = this.params.get('return_to');
      if (this.fromBag) { back.href = this.safeUrl(this.dataset.cartUrl); back.textContent = this.text('return_cart'); }
      else if (returnUrl && this.safeUrl(returnUrl)) { back.href = this.safeUrl(returnUrl); back.textContent = this.text('return_previous'); }
      for (const link of this.querySelectorAll('a[href]')) {
        const url = this.safeUrl(link.getAttribute('href'));
        if (url) link.href = url;
      }
      this.querySelector('[data-count]').setAttribute('aria-live', 'polite');
      this.persist();
      this.classList.add('is-ready');
      this.render();
      this.writeHistory(false);
      this.refreshCart();
    }

    disconnectedCallback() {
      this.events?.abort(); this.unsubscribeCart?.(); this.ready = false;
      if (this.headerFrame) cancelAnimationFrame(this.headerFrame);
      for (const dialog of [this.filterDialog, this.buyDialog]) if (dialog?.open) dialog.close();
    }

    listen(target, name, callback, options = {}) { target.addEventListener(name, callback, { ...options, signal: this.events.signal }); }
    syncHeaderOffset() {
      if (this.headerFrame) return;
      this.headerFrame = requestAnimationFrame(() => {
        this.headerFrame = null;
        const bottom = this.mobile.matches && this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
        this.style.setProperty('--mf-header-bottom', bottom + 'px');
      });
    }
    text(key, values = {}) {
      let value = this.labels[key] || '';
      for (const [name, replacement] of Object.entries(values)) value = value.replaceAll('__' + name + '__', replacement);
      return value;
    }

    safeUrl(value) {
      try {
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?(?:products|collections|cart|search)(?:\/|$)/.test(url.pathname)) return null;
        if (/^\d+$/.test(this.previewId)) url.searchParams.set('preview_theme_id', this.previewId);
        return url.pathname + url.search + url.hash;
      } catch { return null; }
    }

    readSaved() {
      try {
        const value = JSON.parse(sessionStorage.getItem(this.storageKey));
        return value?.version === 1 && Date.now() - value.updatedAt < 12 * 60 * 60 * 1000 ? value : null;
      } catch { return null; }
    }

    persist() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify({ version: 1, updatedAt: Date.now(), filters: this.filters, editions: this.editions })); }
      catch {}
      try {
        sessionStorage.setItem('mo-shop:preferences:' + (window.Shopify?.theme?.id || ''), JSON.stringify({ version: 1, updatedAt: Date.now(), filters: this.filters }));
        document.dispatchEvent(new CustomEvent('mo:preferences-changed'));
      } catch {}
    }

    filtersFromUrl() {
      const params = new URLSearchParams(window.location.search);
      return this.constructor.clean({
        mood: (params.get('bf_mood') || '').split(','), format: (params.get('bf_form') || '').split(','),
        scifi: params.get('bf_world'), read: params.get('bf_read'), availableOnly: params.get('bf_stock') === '1', sort: params.get('bf_sort')
      });
    }

    writeHistory(push) {
      const url = new URL(window.location.href);
      const values = { bf: '1', bf_mood: this.filters.mood.join(','), bf_form: this.filters.format.join(','), bf_world: this.filters.scifi === 'any' ? '' : this.filters.scifi, bf_read: this.filters.read === 'read' ? 'read' : '', bf_stock: this.filters.availableOnly ? '1' : '', bf_sort: this.filters.sort === 'recommended' ? '' : this.filters.sort };
      for (const [name, value] of Object.entries(values)) if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
      if (/^\d+$/.test(this.previewId)) url.searchParams.set('preview_theme_id', this.previewId);
      const state = { ...history.state, moFinderShop: this.storageKey };
      if (push) history.pushState(state, '', url); else history.replaceState(state, '', url);
    }

    placeFilters() {
      if (this.filterDialog.open) this.filterDialog.close();
      const host = this.querySelector(this.mobile.matches ? '[data-filter-mobile-host]' : '[data-filter-desktop-host]');
      host.append(this.filterPanel);
      this.filterPanel.id = this.id + '-panel';
      const toggle = this.querySelector('[data-open-filter]');
      toggle.setAttribute('aria-haspopup', this.mobile.matches ? 'dialog' : 'false');
      toggle.setAttribute('aria-controls', this.mobile.matches ? this.filterDialog.id : this.filterPanel.id);
    }

    openDialog(dialog, trigger) {
      this.lastTrigger = trigger;
      for (const other of [this.filterDialog, this.buyDialog]) if (other !== dialog && other.open) other.close();
      dialog.showModal();
      document.body.classList.add('overflow-hidden');
      const heading = dialog.querySelector('h2');
      if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
    }

    afterDialogClose() {
      if (this.filterDialog.open || this.buyDialog.open || document.querySelector('cart-drawer.active')) return;
      document.body.classList.remove('overflow-hidden');
      if (this.lastTrigger?.isConnected) this.lastTrigger.focus({ preventScroll: true });
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-open-filter')) {
        if (this.mobile.matches) this.openDialog(this.filterDialog, button);
        else { const heading = this.filterPanel.querySelector('h2'); heading.tabIndex = -1; heading.focus(); }
      }
      else if (button.hasAttribute('data-apply-filter') || button.hasAttribute('data-close-filter')) this.filterDialog.close();
      else if (button.hasAttribute('data-close-buy')) this.buyDialog.close();
      else if (button.hasAttribute('data-reset')) {
        this.filters = this.constructor.clean(); this.editions = {}; this.dedications = {}; this.dedicationInput.value = ''; this.relaxedOpen = false;
        this.render(); this.persist(); this.writeHistory(true);
      } else if (button.hasAttribute('data-remove-field')) {
        const { removeField: field, removeValue: value } = button.dataset;
        if (['mood', 'format'].includes(field)) this.filters[field] = this.filters[field].filter(item => item !== value);
        else this.filters[field] = field === 'availableOnly' ? false : field === 'read' ? 'new' : 'any';
        this.changed();
      } else if (button.hasAttribute('data-show-relaxed')) {
        this.relaxedOpen = true; this.render();
        this.relaxed.querySelector('h2').tabIndex = -1; this.relaxed.querySelector('h2').focus();
      } else if (button.hasAttribute('data-open-buy')) {
        const book = this.books.find(item => item.element === button.closest('[data-book]'));
        if (book?.available) this.openPurchase(book, button);
      }
    }

    onChange(event) {
      const input = event.target;
      if (input === this.sortInput) this.filters.sort = input.value;
      else if (input.hasAttribute('data-buy-edition')) {
        const offer = this.currentBook?.offers.find(item => item.id === input.value && item.available);
        if (offer) { this.editions[this.currentBook.id] = offer.id; this.updatePurchase(offer); this.persist(); }
        return;
      } else if (this.filterPanel.contains(input)) {
        if (['mood', 'format'].includes(input.name)) this.filters[input.name] = [...this.filterPanel.querySelectorAll('input[name="' + input.name + '"]:checked')].map(item => item.value);
        else if (['scifi', 'read'].includes(input.name)) this.filters[input.name] = input.value;
        else if (input.name === 'availableOnly') this.filters.availableOnly = input.checked;
        else return;
      } else return;
      this.changed();
    }

    changed() {
      this.filters = this.constructor.clean(this.filters); this.relaxedOpen = false;
      this.render(); this.persist(); this.writeHistory(true);
    }

    covered() {
      const covered = new Set();
      for (const item of this.cart) {
        covered.add(String(item.product_id));
        for (const id of this.coverage[String(item.variant_id)] || this.coverage['p:' + item.product_id] || []) covered.add(String(id));
      }
      return [...covered];
    }

    async refreshCart() {
      if (this.cartRequest) { this.cartRefreshAgain = true; return this.cartRequest; }
      this.cartRequest = (async () => {
        try {
          const response = await fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } });
          if (!response.ok) throw new Error();
          const cart = await response.json();
          this.cart = cart.items.map(item => ({ product_id: item.product_id, variant_id: item.variant_id }));
          if (this.status) this.status.textContent = '';
          this.render();
        } catch {
          if (this.status) this.status.textContent = this.text('cart_refresh_error');
        } finally {
          this.cartRequest = null;
          if (this.cartRefreshAgain) { this.cartRefreshAgain = false; this.refreshCart(); }
        }
      })();
      return this.cartRequest;
    }

    render() {
      const covered = this.covered();
      const context = this.fromCart ? covered : [];
      const result = this.constructor.select(this.books, this.filters, context);
      const count = result.exact.length;
      this.querySelector('[data-count]').textContent = this.text(count === 1 ? 'count_one' : 'count_other', { count });
      this.querySelector('[data-apply-count]').textContent = this.text(count === 1 ? 'apply_one' : 'apply_other', { count });
      this.empty.hidden = count > 0;
      this.relaxed.hidden = count > 0 || !this.relaxedOpen || !result.relaxed.length;
      this.querySelector('[data-show-relaxed]').hidden = count > 0 || !result.relaxed.length || this.relaxedOpen;
      this.sortInput.value = this.filters.sort;
      for (const input of this.filterPanel.querySelectorAll('input')) {
        input.checked = ['mood', 'format'].includes(input.name) ? this.filters[input.name].includes(input.value) : input.name === 'availableOnly' ? this.filters.availableOnly : this.filters[input.name] === input.value;
      }
      this.querySelector('[data-read-group]').hidden = this.filters.format.length > 0 && !this.filters.format.includes('series');
      for (const field of ['mood', 'format', 'scifi', 'read']) {
        const counter = this.querySelector('[data-group-count="' + field + '"]');
        const selected = ['mood', 'format'].includes(field) ? this.filters[field].length : field === 'scifi' ? Number(this.filters.scifi !== 'any') : Number(this.filters.read === 'read');
        if (counter) counter.textContent = selected ? '(' + selected + ')' : '';
      }
      for (const counter of this.querySelectorAll('[data-option-count]')) {
        const field = counter.dataset.field;
        const value = counter.dataset.value;
        const filters = { ...this.filters, [field]: ['mood', 'format'].includes(field) ? [value] : value };
        counter.textContent = this.constructor.select(this.books, filters, context).exact.length;
      }
      this.renderActive();
      for (const book of this.books) {
        book.element.hidden = true;
        this.grid.append(book.element);
        book.element.querySelector('[data-differences]').hidden = true;
        book.element.querySelector('[data-in-cart]').hidden = !covered.includes(book.productId);
        const stock = book.element.querySelector('[data-card-stock]');
        stock.hidden = book.available;
        stock.textContent = this.text('sold_out');
        const priceOffers = book.offers.filter(offer => offer.available);
        const offers = priceOffers.length ? priceOffers : book.offers;
        const cheapest = offers.reduce((a, b) => a.price <= b.price ? a : b);
        book.element.querySelector('[data-card-price]').textContent = new Set(offers.map(offer => offer.price)).size > 1 ? this.text('price_from', { price: cheapest.money }) : cheapest.money;
        book.element.querySelector('[data-card-type]').textContent = this.text('type_' + (book.format === 'series' ? 'series_' + book.volume : book.format));
        const reasons = (this.filters.mood.length ? book.moods.filter(mood => this.filters.mood.includes(mood)) : book.moods).slice(0, 2).map(mood => this.text('mood_' + mood));
        book.element.querySelector('[data-card-reason]').textContent = reasons.join(' · ');
        const buy = book.element.querySelector('[data-open-buy]');
        buy.hidden = false; buy.disabled = !book.available; buy.textContent = this.text(book.available ? 'quick_add' : 'sold_out');
        buy.setAttribute('aria-label', book.title + ' · ' + this.text(book.available ? 'quick_add' : 'sold_out'));
      }
      for (const book of result.exact) { book.element.hidden = false; this.grid.append(book.element); }
      if (!count && this.relaxedOpen) for (const item of result.relaxed) {
        const element = item.book.element;
        const difference = element.querySelector('[data-differences]');
        difference.hidden = false;
        difference.textContent = item.differences.map(field => this.text('different_' + field, {
          actual: field === 'format' ? this.text('format_' + item.book.format) : item.book.moods.map(mood => this.text('mood_' + mood)).join(' / ') || this.text('unconfirmed'),
          wanted: this.filters[field].map(value => this.text(field + '_' + value)).join(' / ')
        })).join(' ');
        element.hidden = false; this.relaxedGrid.append(element);
      }
      if (this.cartReturnTrigger && !this.cartReturnTrigger.getClientRects().length) {
        const countElement = this.querySelector('[data-count]'); countElement.tabIndex = -1;
        document.querySelector('cart-drawer')?.setActiveElement(countElement);
      }
    }

    renderActive() {
      this.active.replaceChildren();
      const filters = [];
      for (const field of ['mood', 'format']) for (const value of this.filters[field]) filters.push({ field, value, label: this.text(field + '_' + value) });
      if (this.filters.scifi !== 'any') filters.push({ field: 'scifi', value: this.filters.scifi, label: this.text('scifi_' + this.filters.scifi) });
      if (this.filters.read === 'read') filters.push({ field: 'read', value: 'read', label: this.text('read_read') });
      if (this.filters.availableOnly) filters.push({ field: 'availableOnly', value: 'true', label: this.text('available_only') });
      for (const filter of filters) {
        const button = document.createElement('button');
        button.type = 'button'; button.className = 'mf-chip';
        button.dataset.removeField = filter.field; button.dataset.removeValue = filter.value;
        button.setAttribute('aria-label', this.text('remove_filter', { value: filter.label }));
        const label = document.createElement('span'); label.textContent = filter.label;
        const close = document.createElement('span'); close.textContent = '×'; close.setAttribute('aria-hidden', 'true');
        button.append(label, close); this.active.append(button);
      }
      for (const reset of this.querySelectorAll('[data-reset]')) reset.disabled = filters.length === 0 && this.filters.sort === 'recommended';
      this.querySelector('[data-reset-top]').hidden = filters.length === 0 && this.filters.sort === 'recommended';
    }

    openPurchase(book, trigger) {
      if (this.currentBook) this.dedications[this.currentBook.id] = this.dedicationInput.value;
      this.currentBook = book;
      this.dedicationInput.value = this.dedications[book.id] || '';
      this.querySelector('[data-buy-title]').textContent = book.title;
      this.querySelector('[data-buy-hook]').textContent = book.hook;
      this.buyForm.querySelector('[role="alert"]').hidden = true;
      const remembered = book.offers.find(offer => offer.id === this.editions[book.id] && offer.available);
      const preferred = book.offers.find(offer => offer.kind === 'book') || book.offers[0];
      const selected = remembered || (preferred.available ? preferred : null);
      this.editionsContainer.querySelectorAll('label').forEach(label => label.remove());
      for (const offer of book.offers) {
        const label = document.createElement('label'); label.className = 'mf-edition';
        const radio = document.createElement('input');
        radio.type = 'radio'; radio.name = 'finder_edition'; radio.value = offer.id;
        radio.disabled = !offer.available; radio.checked = selected?.id === offer.id; radio.setAttribute('data-buy-edition', '');
        const description = document.createElement('span');
        const name = document.createElement('strong'); name.textContent = offer.label;
        const status = document.createElement('small'); status.textContent = offer.money + ' · ' + offer.stock;
        description.append(name, status); label.append(radio, description); this.editionsContainer.append(label);
      }
      const imperfects = this.querySelector('[data-imperfects]');
      imperfects.replaceChildren(); imperfects.hidden = !book.imperfects.length;
      for (const offer of book.imperfects) {
        const link = document.createElement('a'); link.href = this.safeUrl(offer.url);
        link.className = 'mf-text-link'; link.textContent = offer.label + ' · ' + offer.money; imperfects.append(link);
      }
      this.updatePurchase(selected);
      this.openDialog(this.buyDialog, trigger);
    }

    updatePurchase(offer) {
      const fallback = this.currentBook.offers.find(item => item.kind === 'book') || this.currentBook.offers[0];
      const display = offer || fallback;
      this.variantInput.disabled = !offer; this.variantInput.value = offer?.id || '';
      const button = this.buyForm.querySelector('button[type="submit"]');
      button.disabled = !offer; button.querySelector('span').textContent = this.text(offer ? 'add_to_cart' : 'select_available_edition');
      this.querySelector('[data-buy-price]').textContent = offer?.money || '';
      const image = this.querySelector('[data-buy-image]');
      image.src = display.image; image.alt = display.imageAlt || this.currentBook.title;
      this.querySelector('[data-buy-note]').textContent = offer?.note || this.text('select_available_edition');
      this.querySelector('[data-buy-details]').href = this.safeUrl(display.productId === this.currentBook.productId ? display.url : this.currentBook.url);
      this.buyForm.querySelector('[role="alert"]').hidden = true;
    }
  });
}
